<template>
  <!-- Loading Spinner -->
  <div v-if="isLoading" class="flex items-center justify-center h-full my-5">
    <LoadingSpinner />
  </div>

  <!-- Yearly Calendar View -->
  <table v-else class="w-full">
    <thead class="sticky top-0 z-20 bg-neutral-200">
      <tr class="flex">
        <!-- Empty header cell for alignment -->
        <th
          class="sticky left-0 z-30 flex-shrink-0 w-12 p-1 text-center border-b-0 border-r border-solid sm:w-40 border-neutral-200 bg-neutral-100 h-9"
        ></th>
        <!-- Month headers -->
        <th
          v-for="month in monthsInYear"
          :key="`${month.year}-${month.monthNumber}`"
          :style="{ 
            width: `${month.width}px`, 
            marginLeft: month.startOffset > 0 ? `${month.startOffset}px` : '0'
          }"
          class="flex items-center justify-center flex-shrink-0 p-2 text-sm font-bold text-center border-b border-r border-solid border-neutral-200 bg-neutral-100 h-9 text-neutral-600"
        >
          {{ getMonthName(month.monthNumber) }}
          <span v-if="month.year !== currentYear" class="ml-1 text-xs">({{ month.year }})</span>
        </th>
      </tr>
      <tr class="flex">
        <!-- Empty header cell for alignment -->
        <th
          class="sticky left-0 z-30 flex-shrink-0 w-12 p-1 text-center border-b-0 border-r border-solid sm:w-40 border-neutral-200 bg-neutral-100 h-9"
        ></th>
        <!-- Week headers -->
        <th
          v-for="week in weeksInYear"
          :key="week.weekNumber"
          class="flex items-center justify-center flex-shrink-0 p-2 text-sm font-bold text-center border-b border-r border-solid border-neutral-200 bg-neutral-100 w-63 h-9 text-neutral-600"
        >
          <span class="cursor-default">Uge {{ week.weekNumber }}</span>
        </th>
      </tr>
      <tr class="flex">
        <!-- Empty cell for alignment -->
        <th
          class="sticky left-0 z-30 flex-shrink-0 w-12 p-1 text-center border-b border-r border-solid sm:w-40 border-neutral-200 bg-neutral-100"
        ></th>
        <!-- Day headers -->
        <th
          v-for="week in weeksInYear"
          :key="`days-${week.weekNumber}`"
          class="flex items-center justify-center flex-shrink-0 p-0 text-sm text-center border-b border-solid border-neutral-200 bg-neutral-100 w-63 h-9"
        >
          <div class="flex w-full h-full">
            <div
              v-for="(day, index) in week.days"
              :key="index"
              :ref="(el) => { if (isCurrentDay(day.date, day.month, day.year)) currentDayRef = el; }"
              class="flex items-center justify-center w-full h-full font-bold border-r border-solid border-neutral-200 group"
              :class="{ 'text-neutral-600': !isCurrentDay(day.date, day.month, day.year), 
                        'bg-neutral-200 text-neutral-700': isCurrentDay(day.date, day.month, day.year) 
              }"
            >
              <span class="cursor-default group-hover:hidden">{{ day ? day.date : "" }}</span>
              <span class="hidden text-xs cursor-default group-hover:inline">{{ day ? getDayAbbreviation(day.dayOfWeek) : "" }}</span>
            </div>
          </div>
        </th>
      </tr>
    </thead>
    <tbody>
      <tr
        v-for="user in users"
        :key="user.id"
        class="flex"
      >
        <!-- User name cell -->
        <td
          class="sticky left-0 z-10 w-12 sm:w-40 flex-shrink-0 bg-white cursor-pointer text-sm sticky-column border-solid border-neutral-200 border-r border-b h-[37px]"
          @mouseover="showUserInfo(user, $event)"
          @mouseleave="$emit('hide-user-info')"
        >
          <div
            class="px-2 w-full h-full overflow-hidden text-ellipsis whitespace-nowrap leading-[36px]"
            :title="user.first_name + ' ' + user.last_name"
          >
            <span class="sm:hidden">{{ usersStore.getUserInitials(user) }}</span>
            <span class="hidden sm:inline">{{ user.first_name + " " + user.last_name }}</span>
          </div>
        </td>
        <!-- Event cells -->
        <td
          v-for="week in weeksInYear"
          :key="`week-${week.weekNumber}`"
          class="flex flex-shrink-0 border-b border-solid w-63 border-neutral-200"
        >
          <EventCell
            v-for="day in week.days"
            :key="`day-${week.weekNumber}-${day ? day.dayOfWeek : 'empty'}`"
            :day="day"
            :event="getEventForUserAndDay(user, day)"
            :current-year="currentYear"
            @edit-replacement="$emit('edit-replacement', $event)"
            @show-event-info="showEventInfo"
            @hide-event-info="$emit('hide-event-info')"
          />
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script setup>
import { computed, defineEmits, defineProps, ref, defineExpose, onMounted, nextTick, inject } from "vue";
import EventCell from "@/components/ui/AdminDashboard/EventCell.vue";
import LoadingSpinner from "@/components/ui/LoadingSpinner.vue";

// Inject users store
const usersStore = inject("usersStore");

// Props
const props = defineProps({
  currentDate: {
    type: Date,
    required: true,
  },
  users: {
    type: Array,
    required: true,
  },
});

// Emits
const emit = defineEmits([
  "edit-replacement",
  "show-user-info",
  "hide-user-info",
  "show-event-info",
  "hide-event-info",
]);

// Refs
const currentDayRef = ref(null);
const isLoading = ref(true);
const weeksInYear = ref([]);

// Computed properties
const currentYear = computed(() => props.currentDate.getFullYear());
const monthsInYear = computed(() => calculateMonthsInYear(currentYear.value));

/**
 * Calculates all the weeks in a given year.
 * Each week starts on Monday and includes seven days.
 * 
 * @param {number} year - The year for which to calculate weeks.
 * @returns {Promise<Array>} - A promise that resolves to an array of week objects.
 */
function calculateWeeksInYear(year) {
  return new Promise((resolve) => {
    setTimeout(() => {
      const weeks = [];
      let date = new Date(year, 0, 4); // Start with January 4th of the given year
      
      // Move to the Monday of the week containing January 4th
      while (date.getDay() !== 1) {
        date.setDate(date.getDate() - 1);
      }

      let weekNumber = 1;
      const endDate = new Date(year, 11, 29); // End before the first week of next year

      while (date < endDate) {
        const week = {
          weekNumber: weekNumber,
          days: [],
        };

        for (let i = 0; i < 7; i++) {
          week.days.push({
            date: date.getDate(),
            month: date.getMonth(),
            year: date.getFullYear(),
            dayOfWeek: date.getDay(),
          });
          date.setDate(date.getDate() + 1);
        }

        weeks.push(week);
        weekNumber++;
      }

      resolve(weeks);
    }, 0);
  });
}

/**
 * Calculates all the months in a given year with their respective widths and offsets.
 * This is used to align the months correctly in the calendar view.
 * 
 * @param {number} year - The year for which to calculate months.
 * @returns {Array} - An array of month objects with details.
 */
function calculateMonthsInYear(year) {
  const months = [];
  const dayWidth = 36; // Width of each day cell in pixels
  let date = new Date(year, 0, 1); // Start with January 1st of the given year

  // Find the first day of the first week of the year
  let firstWeekStart = new Date(year, 0, 4); // January 4th is always in the first week
  while (firstWeekStart.getDay() !== 1) { // Move to Monday
    firstWeekStart.setDate(firstWeekStart.getDate() - 1);
  }

  while (date.getFullYear() === year) {
    const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
    const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    
    let startOffset = 0;
    let adjustedDaysInMonth = monthEnd.getDate();

    // Calculate offset and adjust days for January
    if (monthStart.getMonth() === 0) { // January
      if (monthStart < firstWeekStart) {
        // If the first day of the month is before the first day of the first week
        const daysDifference = (firstWeekStart - monthStart) / (1000 * 60 * 60 * 24);
        adjustedDaysInMonth -= daysDifference;
      } else if (monthStart > firstWeekStart) {
        // If the first day of the month is after the first day of the first week
        const daysDifference = (monthStart - firstWeekStart) / (1000 * 60 * 60 * 24);
        startOffset = daysDifference * dayWidth;
      }
    }

    // If it's December, check if the last day of the last week is after December 31st
    if (monthStart.getMonth() === 11) { // December
      const lastWeekStart = new Date(year + 1, 0, 4); // January 4th of next year
      while (lastWeekStart.getDay() !== 1) { // Move to Monday
        lastWeekStart.setDate(lastWeekStart.getDate() - 1);
      }
      
      if (lastWeekStart.getFullYear() > year) {
        // If the last week starts in the next year, use December 31st
        adjustedDaysInMonth = 31;
      } else {
        // Otherwise, find the last Sunday as before
        let lastSunday = new Date(year, 11, 31);
        while (lastSunday.getDay() !== 0) { // 0 = Sunday
          lastSunday.setDate(lastSunday.getDate() - 1);
        }
        adjustedDaysInMonth = lastSunday.getDate();
      }
    }

    const monthWidth = adjustedDaysInMonth * dayWidth;

    months.push({
      monthNumber: monthStart.getMonth(),
      year: monthStart.getFullYear(),
      startOffset: startOffset,
      width: monthWidth,
    });

    date.setMonth(date.getMonth() + 1); // Move to the next month
  }

  return months;
}

/**
 * Retrieves the abbreviated name of a day based on its numeric representation.
 * 
 * @param {number} day - The numeric representation of the day (0-6).
 * @returns {string} - The abbreviated name of the day.
 */
function getDayAbbreviation(day) {
  const days = ["Søn", "Man", "Tir", "Ons", "Tor", "Fre", "Lør"];
  return days[day];
}

/**
 * Retrieves the full name of a month based on its numeric representation.
 * 
 * @param {number} month - The numeric representation of the month (0-11).
 * @returns {string} - The full name of the month.
 */
function getMonthName(month) {
  const months = [
    "Januar",
    "Februar",
    "Marts",
    "April",
    "Maj",
    "Juni",
    "Juli",
    "August",
    "September",
    "Oktober",
    "November",
    "December",
  ];
  return months[month];
}

/**
 * Determines if a given day is the current day.
 * 
 * @param {number} day - The day of the month.
 * @param {number} month - The month (0-11).
 * @param {number} year - The full year (e.g., 2023).
 * @returns {boolean} - True if the given day is today, false otherwise.
 */
const isCurrentDay = (day, month, year) => {
  const today = new Date();
  return (
    today.getDate() === day &&
    today.getMonth() === month &&
    today.getFullYear() === year
  );
};

/**
 * Retrieves the event associated with a specific user and day.
 * 
 * @param {Object} user - The user object containing events.
 * @param {Object} day - The day object containing date information.
 * @returns {Object|null} - The event object if found, otherwise null.
 */
function getEventForUserAndDay(user, day) {
  return user.events.find((event) => {
    const eventDate = new Date(event.date);
    return (
      eventDate.getDate() === day.date &&
      eventDate.getMonth() === day.month &&
      eventDate.getFullYear() === day.year
    );
  });
}

/**
 * Emits an event to show user information when a user cell is hovered.
 * 
 * @param {Object} user - The user object.
 * @param {Event} event - The mouse event.
 */
function showUserInfo(user, event) {
  emit("show-user-info", user, event.clientX, event.clientY);
}

/**
 * Emits an event to show event information when an event is hovered.
 * 
 * @param {Object} event - The event object.
 * @param {number} clientX - The X coordinate of the mouse.
 * @param {number} clientY - The Y coordinate of the mouse.
 */
function showEventInfo(event, clientX, clientY) {
  emit("show-event-info", event, clientX, clientY);
}

/**
 * Scrolls the calendar to ensure the current day is visible in the viewport.
 */
const scrollToCurrentDay = () => {
  if (currentDayRef.value) {
    const padding = 160;
    const container = currentDayRef.value.closest(".overflow-y-auto");
    if (container) {
      const containerRect = container.getBoundingClientRect();
      const currentDayRect = currentDayRef.value.getBoundingClientRect();
      const scrollLeft =
        currentDayRect.left -
        containerRect.left +
        container.scrollLeft -
        padding;

      container.scrollTo({
        left: scrollLeft,
        behavior: "smooth",
      });
    }
  }
};

// Lifecycle hook - runs when the component is mounted
onMounted(async () => {
  // Calculate all weeks for the current year
  weeksInYear.value = await calculateWeeksInYear(currentYear.value);
  
  // Set loading to false once weeks are calculated
  isLoading.value = false;
  
  // Scroll to the current day after the DOM updates
  nextTick(() => {
    scrollToCurrentDay();
  });
});

// Expose the scrollToCurrentDay method to parent components if needed
defineExpose({ scrollToCurrentDay });
</script>

<style scoped>
.w-63 {
  width: 15.75rem; /* 7 * 2.25rem (w-9) */
}
</style>